import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Link, 
  InputAdornment, 
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  LockOutlined as LockIcon 
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import TwoFactorVerification from '../components/auth/TwoFactorVerification';

interface FormData {
  email: string;
  password: string;
}

interface FormErrors {
  email?: string;
  password?: string;
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [userId, setUserId] = useState<string>('');
  
  // Check if there's a stored AI prompt
  const hasStoredPrompt = localStorage.getItem('aiPrompt') !== null;

  // Get the feature parameter from URL if it exists
  const queryParams = new URLSearchParams(location.search);
  const featureParam = queryParams.get('feature');

  // Get redirect path from location state or use documents/upload if there's a stored prompt, otherwise default to dashboard
  const from = location.state?.from?.pathname || (hasStoredPrompt ? '/documents/upload?tab=ai' : '/dashboard');
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Clear error when field is edited
    if (formErrors[name as keyof FormErrors]) {
      setFormErrors({
        ...formErrors,
        [name]: undefined,
      });
    }
    
    // Clear general error
    if (error) {
      setError('');
    }
  };
  
  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    let isValid = true;
    
    // Validate email
    if (!formData.email) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
      isValid = false;
    }
    
    // Validate password
    if (!formData.password) {
      errors.password = 'Password is required';
      isValid = false;
    }
    
    setFormErrors(errors);
    return isValid;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      console.log('Attempting login with:', formData.email, formData.password);
      // Use the login function from AuthContext
      const success = await login(formData.email, formData.password);
      console.log('Login success:', success);
      
      if (success) {
        // Navigate to the appropriate destination based on parameters
        // Clear any stored prompt only after successful navigation
        // This ensures the stored AI prompt doesn't get lost on failed logins
        
        // Determine where to redirect based on the feature parameter
        if (featureParam === 'upload') {
          navigate('/documents/upload');
        } else if (featureParam === 'ai-generation') {
          navigate('/documents/upload?tab=ai');
        } else if (featureParam === 'smart-editing') {
          navigate('/documents/upload?tab=edit');
        } else if (hasStoredPrompt) {
          // If no specific feature but there's a stored prompt
          navigate('/documents/upload?tab=ai');
        } else {
          // Default fallback
          navigate(from, { replace: true });
        }
      } else {
        setError('Login failed. Please check your credentials.');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      if (err.response && err.response.data && err.response.data.message) {
        setError(err.response.data.message);
      } else {
        setError('Login failed. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  
  const handle2FAVerify = async (userId: string, verified: boolean) => {
    if (!verified) {
      setError('Two-factor authentication failed.');
      setRequires2FA(false);
      return;
    }
    
    setLoading(true);
    
    try {
      // For the demo, we'll just simulate a successful 2FA verification
      // In a real app, this would call an API endpoint
      localStorage.setItem('token', 'demo-2fa-token');
      
      // Redirect to the appropriate page
      if (featureParam === 'upload') {
        navigate('/documents/upload');
      } else if (featureParam === 'ai-generation') {
        navigate('/documents/upload?tab=ai');
      } else if (featureParam === 'smart-editing') {
        navigate('/documents/upload?tab=edit');
      } else if (hasStoredPrompt) {
        navigate('/documents/upload?tab=ai');
      } else {
        navigate(from);
      }
    } catch (err: any) {
      setError('Authentication failed.');
      setRequires2FA(false);
    } finally {
      setLoading(false);
    }
  };
  
  const handle2FACancel = () => {
    setRequires2FA(false);
    setUserId('');
  };
  
  // If 2FA verification is required, show the 2FA component
  if (requires2FA) {
    return (
      <TwoFactorVerification 
        userId={userId} 
        onVerify={handle2FAVerify} 
        onCancel={handle2FACancel} 
      />
    );
  }
  
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        maxWidth: 450,
        mx: 'auto',
        py: 8,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
          <LockIcon color="primary" fontSize="large" sx={{ mb: 1 }} />
          <Typography component="h1" variant="h5">
            Sign in to Contract AI
          </Typography>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3, width: '100%' }}>
            {error}
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            value={formData.email}
            onChange={handleChange}
            error={!!formErrors.email}
            helperText={formErrors.email}
          />
          
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            value={formData.password}
            onChange={handleChange}
            error={!!formErrors.password}
            helperText={formErrors.password}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          <Box sx={{ mt: 1, textAlign: 'right' }}>
            <Link component={RouterLink} to="/forgot-password" variant="body2">
              Forgot password?
            </Link>
          </Box>
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 3, mb: 2, py: 1.5 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Sign In'}
          </Button>
          
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            sx={{ mb: 2 }}
            onClick={() => {
              setFormData({
                email: 'admin@contractai.com',
                password: 'Admin123!'
              });
              setFormErrors({});
              setError('');
            }}
          >
            Use Demo Credentials
          </Button>
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2">
              Don't have an account?{' '}
              <Link component={RouterLink} to="/register" variant="body2">
                Sign up
              </Link>
            </Typography>
          </Box>
        </Box>
      </Paper>
      
      {/* Demo credentials */}
      <Paper sx={{ p: 2, mt: 3, bgcolor: 'background.default', width: '100%' }}>
        <Typography variant="body2" align="center" color="textSecondary">
          For demo purposes, use: email: <strong>admin@contractai.com</strong> / password: <strong>Admin123!</strong>
        </Typography>
      </Paper>
    </Box>
  );
};

export default LoginPage; 